import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import type { Machine } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { PageHeader } from "../../components/layout/PageHeader";
import {
  InfoIcon,
  KeyIcon,
  RefreshIcon,
  SearchIcon,
  TrashIcon,
} from "../../components/layout/icons";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Panel } from "../../components/ui/Panel";
import { EmptyState, ErrorState } from "../../components/ui/States";
import { StatusDot } from "../../components/ui/StatusDot";
import { machineTone } from "../../components/ui/status";
import { Table, type Column } from "../../components/ui/Table";
import { TableSkeleton } from "../../components/ui/TableSkeleton";
import { absoluteTime, relativeTime } from "../../lib/time";
import "./machines.css";
import { BulkRemoveMachinesDialog } from "./BulkRemoveMachinesDialog";
import { MachineDetailDrawer } from "./MachineDetailDrawer";
import { MachineKeysModal } from "./MachineKeysModal";
import { RemoveMachineDialog } from "./RemoveMachineDialog";
import { useMachines } from "./hooks";

export function MachinesPage() {
  const { isAdmin } = useAuth();
  const machinesQuery = useMachines();
  const [filter, setFilter] = useState("");

  const [detailFor, setDetailFor] = useState<Machine | null>(null);
  const [keysFor, setKeysFor] = useState<Machine | null>(null);
  const [removeFor, setRemoveFor] = useState<Machine | null>(null);

  // Bulk-select state (admin only). Keyed by agent_id so selection survives the
  // 20s poll re-ordering. A stale id (machine removed elsewhere) is harmless —
  // it is reconciled against the current list before any action.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const { data } = machinesQuery;
  const machines = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return machines;
    return machines.filter(
      (m) =>
        m.hostname.toLowerCase().includes(q) ||
        m.agent_id.toLowerCase().includes(q),
    );
  }, [machines, filter]);

  const onlineCount = useMemo(
    () => machines.filter((m) => m.status === "online").length,
    [machines],
  );

  // Selection is scoped to the currently-visible (filtered) rows.
  const selectedVisible = useMemo(
    () => filtered.filter((m) => selected.has(m.agent_id)),
    [filtered, selected],
  );
  const allVisibleSelected =
    filtered.length > 0 && selectedVisible.length === filtered.length;
  const someVisibleSelected =
    selectedVisible.length > 0 && !allVisibleSelected;

  // Drop selections that no longer exist (removed, or filtered away by a poll
  // that dropped the machine) so the "Remove selected (N)" count stays truthful.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(machines.map((m) => m.agent_id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [machines]);

  // Reflect the partial state on the header checkbox (indeterminate is DOM-only).
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  function toggleOne(agentId: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(agentId);
      else next.delete(agentId);
      return next;
    });
  }

  function toggleAllVisible(checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of filtered) {
        if (checked) next.add(m.agent_id);
        else next.delete(m.agent_id);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const columns: Column<Machine>[] = [
    // Admin-only selection rail. Built conditionally so non-admins never see it.
    ...(isAdmin
      ? [
          {
            key: "select",
            cellClass: "dt__select",
            header: (
              <label className="dt__checkwrap">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="dt__check"
                  checked={allVisibleSelected}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  aria-label={
                    allVisibleSelected
                      ? "Deselect all machines"
                      : "Select all machines"
                  }
                />
              </label>
            ),
            render: (m: Machine) => (
              <label
                className="dt__checkwrap"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="dt__check"
                  checked={selected.has(m.agent_id)}
                  onChange={(e) => toggleOne(m.agent_id, e.target.checked)}
                  aria-label={`Select ${m.hostname}`}
                />
              </label>
            ),
          } satisfies Column<Machine>,
        ]
      : []),
    {
      key: "status",
      header: "",
      cellClass: "dt__status",
      render: (m) => (
        <StatusDot tone={machineTone(m.status)} label={m.status} />
      ),
    },
    {
      key: "hostname",
      header: "Hostname",
      render: (m) => <span className="dt__strong">{m.hostname}</span>,
    },
    {
      key: "os",
      header: "OS",
      render: (m) => (
        <span className="dt__muted">{m.os_version ?? "Unknown"}</span>
      ),
    },
    {
      key: "mode",
      header: "Mode",
      render: (m) =>
        m.is_persistent ? (
          <Badge tone="accent">Persistent</Badge>
        ) : (
          <Badge tone="neutral">Attended</Badge>
        ),
    },
    {
      key: "last_seen",
      header: "Last seen",
      render: (m) => (
        <span className="dt__mono" title={absoluteTime(m.last_seen)}>
          {relativeTime(m.last_seen)}
        </span>
      ),
    },
    {
      key: "agent_id",
      header: "Agent ID",
      render: (m) => (
        <span className="dt__mono" title={m.agent_id}>
          {m.agent_id}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      cellClass: "dt__actions",
      render: (m) => (
        <span
          className="dt__rowactions"
          // Row actions shouldn't trigger the row's open-detail click.
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDetailFor(m)}
            aria-label={`View detail for ${m.hostname}`}
          >
            <InfoIcon width={14} height={14} />
            Detail
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setKeysFor(m)}
              aria-label={`Manage keys for ${m.hostname}`}
            >
              <KeyIcon width={14} height={14} />
              Keys
            </Button>
          )}
          {/* Removal is admin-only, mirroring the server's 403 for non-admins. */}
          {isAdmin && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setRemoveFor(m)}
              aria-label={`Remove ${m.hostname}`}
            >
              <TrashIcon width={14} height={14} />
              Remove
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Machines"
        subtitle="Registered agents across all managed endpoints."
        actions={
          <Button
            variant="ghost"
            onClick={() => void machinesQuery.refetch()}
            loading={machinesQuery.isFetching}
          >
            <RefreshIcon width={15} height={15} />
            Refresh
          </Button>
        }
      />

      <Panel flush>
        <div style={{ padding: "14px 16px 0" }}>
          <div className="toolbar">
            <div className="searchbox">
              <span className="searchbox__icon">
                <SearchIcon width={15} height={15} />
              </span>
              <Input
                placeholder="Filter by hostname or agent ID"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                aria-label="Filter machines"
              />
            </div>
            {/* Bar count tracks the VISIBLE selected rows — the exact set the
                bulk dialog will state and remove. Under an active filter that
                hides some selected rows, this keeps bar == dialog == removed.
                Keyed off selectedVisible so a "Remove selected (0)" bar never
                shows when the only selections are filtered out of view. */}
            {isAdmin && selectedVisible.length > 0 ? (
              <div className="bulkbar" role="status">
                <span className="bulkbar__count">
                  <span className="mono">{selectedVisible.length}</span> selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  aria-label="Clear selection"
                >
                  Clear
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setBulkOpen(true)}
                  aria-label={`Remove ${selectedVisible.length} selected machines`}
                >
                  <TrashIcon width={14} height={14} />
                  Remove selected ({selectedVisible.length})
                </Button>
              </div>
            ) : (
              <div className="toolbar__count">
                <span className="mono">{onlineCount}</span> online ·{" "}
                <span className="mono">{machines.length}</span> total
              </div>
            )}
          </div>
        </div>

        {machinesQuery.isLoading ? (
          <>
            <span className="visually-hidden" role="status">
              Loading machines
            </span>
            <TableSkeleton
              headers={
                isAdmin
                  ? ["", "", "Hostname", "OS", "Mode", "Last seen", "Agent ID", ""]
                  : ["", "Hostname", "OS", "Mode", "Last seen", "Agent ID", ""]
              }
            />
          </>
        ) : machinesQuery.isError ? (
          <ErrorState
            title="Could not load machines"
            message={
              machinesQuery.error instanceof ApiError
                ? machinesQuery.error.message
                : "The GuruConnect server did not respond. Check the relay status, then retry."
            }
            action={
              <Button variant="primary" onClick={() => void machinesQuery.refetch()}>
                Retry
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          filter ? (
            <EmptyState
              title="No matching machines"
              message={`Nothing matches "${filter}". Clear the filter to see every registered machine.`}
              action={
                <Button variant="ghost" onClick={() => setFilter("")}>
                  Clear filter
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No machines registered yet"
              message="Agents appear here the moment they enroll with the relay. Install the GuruConnect agent on an endpoint to get started."
            />
          )
        ) : (
          <Table
            columns={columns}
            rows={filtered}
            rowKey={(m) => m.id}
            onRowClick={setDetailFor}
            rowLabel={(m) => m.hostname}
          />
        )}
      </Panel>

      <MachineDetailDrawer machine={detailFor} onClose={() => setDetailFor(null)} />
      {isAdmin && (
        <MachineKeysModal machine={keysFor} onClose={() => setKeysFor(null)} />
      )}
      {isAdmin && (
        <RemoveMachineDialog
          machine={removeFor}
          onClose={() => setRemoveFor(null)}
        />
      )}
      {isAdmin && (
        <BulkRemoveMachinesDialog
          open={bulkOpen}
          agentIds={selectedVisible.map((m) => m.agent_id)}
          onClose={() => setBulkOpen(false)}
          onRemoved={clearSelection}
        />
      )}
    </div>
  );
}
